import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CartSkeleton from "../components/CartSkeleton";

const Container = styled.div`
  /* min-height: 100vh; */
  background: #fff;
`;

const CartWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: 60vh;
  overflow: hidden;
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const CartHeading = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #000;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 80px 120px 50px;
  gap: 25px;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  align-items: center;
  background: #fff;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 992px) {
    grid-template-columns: 100px 1fr 80px;
    gap: 15px;
    padding: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 100px 1fr;
    gap: 20px 15px;
    padding: 15px;
    align-items: start;

    /* First row: Image | Details */
    > :nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }

    > :nth-child(2) {
      grid-column: 2;
      grid-row: 1;
    }

    /* Second row: Quantity | DeleteButton */
    > :nth-child(3) {
      grid-column: 1 / 2;
      grid-row: 2;
      margin-top: 10px;
    }

    > :nth-child(5) {
      grid-column: 2;
      grid-row: 2;
      justify-self: end;
      margin-top: 10px;
    }

    /* Third row: ItemTotal spans full width */
    > :nth-child(4) {
      grid-column: 1 / -1;
      grid-row: 3;
      text-align: right;
      margin-top: 10px;
      border-top: 1px solid #eee;
      padding-top: 12px;
    }
  }
`;

const ItemImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  /* background: #f5f5f5; */
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const ItemName = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;
  line-height: 1.3;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    /* color: #fc8c04; */
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ItemPrice = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ItemMeta = styled.p`
  font-size: 12px;
  color: #999;
  margin: 0;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100px;
  height: 40px;
  border: 2px solid #ddd;
  border-radius: 6px;
  background: #fff;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 90px;
    height: 36px;
  }
`;

const QuantityButton = styled.button`
  background: none;
  border: none;
  width: 33.33%;
  height: 100%;
  cursor: pointer;
  color: #666;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-weight: 700;
  border-right: 2px solid #ddd;

  &:last-child {
    border-right: none;
  }

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #000;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const QuantityDisplay = styled.span`
  width: 33.33%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #333;
`;

const Loader = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ItemTotal = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  text-align: right;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #ccc;
  font-size: 18px;
  transition: all 0.3s;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;

  &:hover:not(:disabled) {
    color: #ff4444;
    background: #ffe6e6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    width: 36px;
    height: 36px;
  }
`;

const ContinueShopping = styled.a`
  font-size: 14px;
  color: #000;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  transition: all 0.3s;
  font-weight: 500;

  &:hover {
    color: #666;
    gap: 12px;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    margin-top: 15px;
  }
`;

const OrderSummary = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  height: fit-content;
  position: sticky;
  top: 20px;

  @media (max-width: 992px) {
    padding: 25px;
  }

  @media (max-width: 768px) {
    position: static;
    padding: 20px;
    margin-top: 20px;
  }
`;

const SummarySection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  &.total-row {
    border-bottom: 2px solid #000;
    border-top: 2px solid #000;
    padding: 18px 0;
    font-size: 16px;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    padding: 12px 0;
    font-size: 13px;

    &.total-row {
      padding: 15px 0;
      font-size: 15px;
    }
  }
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #000;
  font-weight: 600;
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.3s;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 14px;
    font-size: 13px;
    margin-top: 18px;
  }
`;

const PolicySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const PolicyItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;

  .icon {
    font-size: 18px;
    color: #000;
    flex-shrink: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .title {
    font-weight: 600;
    color: #000;
  }

  .subtitle {
    color: #666;
    font-size: 12px;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 60px 20px;

  h2 {
    font-size: 24px;
    margin-bottom: 16px;
    color: #000;
  }

  p {
    color: #666;
    margin-bottom: 24px;
  }
`;

const EmptyButton = styled.a`
  display: inline-block;
  padding: 12px 30px;
  background: #000;
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  transition: 0.3s;

  &:hover {
    background: #333;
  }
`;

const ErrorMessage = styled.div`
  padding: 20px;
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin-bottom: 20px;
  text-align: center;
`;

const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmationDialog = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 90%;
  text-align: center;

  h3 {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 15px 0;
    color: #000;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0 0 25px 0;
  }
`;

const DialogButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
`;

const ConfirmButton = styled.button`
  padding: 10px 25px;
  background: #ff4444;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #cc0000;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 25px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  min-width: 100px;

  &:hover {
    background: #e9e9e9;
  }
`;

const Cart = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [stockErrors, setStockErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletionErrors, setDeletionErrors] = useState({});

  const TAX_RATE = 0.18;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch cart from API
  useEffect(() => {
    const fetchCart = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/cart`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status && data.data?.items) {
          // Map API response to cartItems format
          const mappedItems = data.data.items.map((item) => ({
            id: item.id,
            variantId: item.product_variant.id,
            name: item.product_variant.product.name,
            slug: item.product_variant.product.slug,
            price: parseFloat(item.product_variant.price),
            quantity: item.quantity,
            image: item.product_variant.product.images?.[0]?.url,
            size: item.product_variant.size?.name || "N/A",
            color: item.product_variant.color?.name || "N/A",
            productId: item.product_variant.product.id,
          }));
          setCartItems(mappedItems);
        }
      } catch (err) {
        console.error("Error fetching cart:", err);
        setError("Failed to load cart. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [token, API_BASE_URL]);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const SHIPPING_COST = subtotal > 499 ? 0 : 100;
  const tax = 0;
  const total = subtotal + SHIPPING_COST;

  const handleQuantityChange = async (id, newQuantity) => {
    if (newQuantity < 1) return;

    // Add to updating set
    setUpdatingItems((prev) => new Set(prev).add(id));
    // Clear previous stock error for this item
    setStockErrors((prev) => ({ ...prev, [id]: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/cart/items/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quantity: newQuantity }),
      });

      const data = await response.json();

      if (data.status) {
        // Successfully updated, update local state
        setCartItems(
          cartItems.map((item) =>
            item.id === id ? { ...item, quantity: newQuantity } : item,
          ),
        );
        // Clear any previous error
        setStockErrors((prev) => ({ ...prev, [id]: null }));
      } else if (!data.status) {
        // Handle error - likely insufficient stock
        if (data.data?.available_qty !== undefined) {
          const errorMsg = `Only ${data.data.available_qty} items available in stock`;
          setStockErrors((prev) => ({ ...prev, [id]: errorMsg }));
        } else {
          setStockErrors((prev) => ({
            ...prev,
            [id]: data.message || "Failed to update quantity",
          }));
        }
      }
    } catch (err) {
      console.error("Error updating quantity:", err);
      setStockErrors((prev) => ({
        ...prev,
        [id]: "Failed to update quantity. Please try again.",
      }));
    } finally {
      // Remove from updating set
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleRemoveItem = async (id) => {
    setUpdatingItems((prev) => new Set(prev).add(id));
    setDeletionErrors((prev) => ({ ...prev, [id]: null }));

    try {
      const response = await fetch(`${API_BASE_URL}/cart/items/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status) {
        // Successfully removed
        setCartItems(cartItems.filter((item) => item.id !== id));
        setStockErrors((prev) => ({ ...prev, [id]: null }));
        setConfirmDelete(null);
      } else {
        // Show error message
        const errorMsg =
          data.message || "Failed to remove item. Please try again.";
        setDeletionErrors((prev) => ({ ...prev, [id]: errorMsg }));
      }
    } catch (err) {
      console.error("Error removing item:", err);
      setDeletionErrors((prev) => ({
        ...prev,
        [id]: "Failed to remove item. Please try again.",
      }));
    } finally {
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const initiateDelete = (id) => {
    setConfirmDelete(id);
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const handleViewProduct = (product) => {
    navigate("/product/" + product.slug, {
      state: { product: { ...product, id: product.productId } },
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <Container>
        {/* <Navbar /> */}
        <CartWrapper>
          <CartHeading>Shopping Cart</CartHeading>
          <CartSkeleton />
        </CartWrapper>
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <CartWrapper>
        <CartHeading>Shopping Cart</CartHeading>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {cartItems.length > 0 ? (
          <CartContent>
            <CartItems>
              {cartItems.map((item) => (
                <div key={item.id}>
                  <CartItem>
                    <ItemImage onClick={() => handleViewProduct(item)}>
                      <img src={item.image} alt={item.name} />
                    </ItemImage>

                    <ItemDetails>
                      <ItemName onClick={() => handleViewProduct(item)}>
                        {item.name}
                      </ItemName>
                      <ItemPrice>
                        ₹{item.price.toLocaleString("en-IN")}
                      </ItemPrice>
                      {item.size && <ItemMeta>Size: {item.size}</ItemMeta>}
                      {item.color && <ItemMeta>Color: {item.color}</ItemMeta>}
                    </ItemDetails>

                    <QuantityControl>
                      <QuantityButton
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity - 1)
                        }
                        disabled={
                          item.quantity <= 1 || updatingItems.has(item.id)
                        }
                      >
                        −
                      </QuantityButton>
                      <QuantityDisplay>
                        {updatingItems.has(item.id) ? (
                          <Loader />
                        ) : (
                          item.quantity
                        )}
                      </QuantityDisplay>
                      <QuantityButton
                        onClick={() =>
                          handleQuantityChange(item.id, item.quantity + 1)
                        }
                        disabled={updatingItems.has(item.id)}
                      >
                        +
                      </QuantityButton>
                    </QuantityControl>

                    <ItemTotal>
                      ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                    </ItemTotal>

                    <DeleteButton
                      onClick={() => initiateDelete(item.id)}
                      disabled={updatingItems.has(item.id)}
                      title="Remove item"
                    >
                      {updatingItems.has(item.id) ? (
                        <Loader />
                      ) : (
                        <DeleteOutlined />
                      )}
                    </DeleteButton>
                  </CartItem>
                  {stockErrors[item.id] && (
                    <ErrorMessage>{stockErrors[item.id]}</ErrorMessage>
                  )}
                  {deletionErrors[item.id] && (
                    <ErrorMessage>{deletionErrors[item.id]}</ErrorMessage>
                  )}
                </div>
              ))}

              {confirmDelete !== null && (
                <ConfirmationOverlay onClick={cancelDelete}>
                  <ConfirmationDialog onClick={(e) => e.stopPropagation()}>
                    <h3>Delete Item?</h3>
                    <p>
                      Are you sure you want to remove this item from your cart?
                    </p>
                    <DialogButtons>
                      <ConfirmButton
                        onClick={() => handleRemoveItem(confirmDelete)}
                        disabled={updatingItems.has(confirmDelete)}
                      >
                        {updatingItems.has(confirmDelete) ? (
                          <Loader />
                        ) : (
                          "Yes, Delete"
                        )}
                      </ConfirmButton>
                      <CancelButton
                        onClick={cancelDelete}
                        disabled={updatingItems.has(confirmDelete)}
                      >
                        No, Cancel
                      </CancelButton>
                    </DialogButtons>
                  </ConfirmationDialog>
                </ConfirmationOverlay>
              )}

              <ContinueShopping href="/shop">
                ← Continue shopping
              </ContinueShopping>
            </CartItems>

            <OrderSummary>
              <SummarySection>
                <SummaryLabel>{cartItems.length} items</SummaryLabel>
                <SummaryValue>₹{subtotal.toLocaleString("en-IN")}</SummaryValue>
              </SummarySection>

              <SummarySection>
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <SummaryLabel>Shipping</SummaryLabel>
                    <SummaryValue>
                      {SHIPPING_COST === 0 ? (
                        <span style={{ color: "#28a745" }}>FREE</span>
                      ) : (
                        `₹${SHIPPING_COST.toLocaleString("en-IN")}`
                      )}
                    </SummaryValue>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#999",
                      fontWeight: "500",
                    }}
                  >
                    {SHIPPING_COST === 0 ? (
                      <span>✓ FREE Shipping on orders above ₹499</span>
                    ) : (
                      <span>
                        Add ₹
                        {(500 - Math.ceil(subtotal)).toLocaleString("en-IN")}{" "}
                        more for free shipping
                      </span>
                    )}
                  </div>
                </div>
              </SummarySection>

              <SummarySection className="total-row">
                <SummaryLabel>Total</SummaryLabel>
                <SummaryValue>₹{total.toLocaleString("en-IN")}</SummaryValue>
              </SummarySection>

              <CheckoutButton
                onClick={() => {
                  navigate("/checkout");
                }}
              >
                CHECKOUT
              </CheckoutButton>

              <PolicySection>
                <PolicyItem>
                  <div className="icon">🔒</div>
                  <div className="content">
                    <div className="title">Security policy</div>
                    <div className="subtitle">
                      Edit with the Customer Reassurance module
                    </div>
                  </div>
                </PolicyItem>

                <PolicyItem>
                  <div className="icon">🚚</div>
                  <div className="content">
                    <div className="title">Delivery policy</div>
                    <div className="subtitle">
                      Edit with the Customer Reassurance module
                    </div>
                  </div>
                </PolicyItem>

                <PolicyItem>
                  <div className="icon">👍</div>
                  <div className="content">
                    <div className="title">Return policy</div>
                    <div className="subtitle">
                      Edit with the Customer Reassurance module
                    </div>
                  </div>
                </PolicyItem>
              </PolicySection>
            </OrderSummary>
          </CartContent>
        ) : (
          <EmptyCart>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything to your cart yet.</p>
            <EmptyButton href="/shop">Start Shopping</EmptyButton>
          </EmptyCart>
        )}
      </CartWrapper>

      <Footer />
    </Container>
  );
};

export default Cart;
